/* Patterns (MAR-372 hardening) — the documented patterns and small text roles screens were
   rebuilding out of the library's internal classes. Each renders classes that already live in
   components.css / settings.css; a screen composes these and never writes the class itself.
   Appended to the library through `export * from './Patterns'` in ./index.jsx. */
import * as React from 'react';
import { Orb } from './index.jsx';

const cx = (...a) => a.filter(Boolean).join(' ');

/* ---------------- Reading drawer (Layouts README, D-36) ----------------
   `Drawer size="lg"` › `Reading` › `ReadingSection`s: a record told as a story, 22px between
   labelled sections, one column. */
function Reading({ children, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-reading', className)}>{children}</div>;
}
function ReadingSection({ label, testId, children, className = '', ...rest }) {
  return (
    <section {...rest} className={cx('mv-reading-sec', className)} data-testid={testId ?? rest['data-testid']}>
      {label != null && label !== false && label !== '' && <div className="mv-reading-label">{label}</div>}
      {children}
    </section>
  );
}

/* ---------------- Text roles ---------------- */
/** A quiet line under a control or a section: what is absent, what a value means. */
function Hint({ as: Tag = 'p', children, className = '', ...rest }) {
  return <Tag {...rest} className={cx('mv-hint', className)}>{children}</Tag>;
}
/** A visible label for a control group that is not a Field (a chip set, a segmented choice,
 *  a checkbox group's `aria-labelledby` target). A Field labels itself. */
function FieldLabel({ as: Tag = 'span', children, className = '', ...rest }) {
  return <Tag {...rest} className={cx('mv-label', className)}>{children}</Tag>;
}
/** A message's text as it was written — plain, wrapped, line breaks kept — outside a Thread
 *  or Transcript (a decision's working, a sent message on a Card). */
function MessageBody({ children, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-bubble-plain', className)}>{children}</div>;
}
/** The quiet stand-in for a value that is absent ("—", "nothing recorded"). KeyValue uses it
 *  for a null value; use it where a value renders outside a KeyValue row. */
function EmptyValue({ children = '—', className = '', ...rest }) {
  return <span {...rest} className={cx('mv-kv-empty', className)}>{children}</span>;
}

/** Secondary mono text — a timestamp, a "formed <date>" line, an id line, a request id, a
 *  count (the `t-timestamp` style: mono 10.5px, ink-soft). `dateTime` renders a <time>. */
function Meta({ as, dateTime, children, className = '', ...rest }) {
  const Tag = dateTime != null ? 'time' : as || 'span';
  return <Tag {...rest} dateTime={dateTime != null ? dateTime : undefined} className={cx('mv-meta', className)}>{children}</Tag>;
}
/** Inline code — a snippet, a tag name, a discount code — in running text (mono 12px, a
 *  bg-2 pill). A block of code is `CodeBlock`. */
function Code({ children, className = '', ...rest }) {
  return <code {...rest} className={cx('mv-code', className)}>{children}</code>;
}

/* ---------------- Boot loader (overlays.md: z-loader, opaque) ---------------- */
function BootLoader({ caption = 'Catching up on your store…', hidden = false, className = '' }) {
  return (
    <div className={cx('mv-loader', hidden && 'mv-loader--hidden', className)} role="status" aria-live="polite" aria-hidden={hidden || undefined}>
      <Orb size={56} float />
      <div className="mv-loader-mark">Marva</div>
      {caption && <p className="mv-loader-cap">{caption}</p>}
    </div>
  );
}

/* ---------------- Article pager (Layouts README: previous / next are real links in a nav) ----------------
   `onClick` gets the event and decides (a modified click should still open a new tab). */
function ArticlePager({ prev, next, label = 'More articles', prevLabel = '← Previous', nextLabel = 'Next →', className = '' }) {
  if (!prev && !next) return null;
  const link = (it, cap, isNext) => (
    <a className={isNext ? 'next' : undefined} href={it.href}
      onClick={it.onClick}>
      <span className="l">{cap}</span>
      <span className="t">{it.title}</span>
    </a>
  );
  return (
    <nav className={cx('mv-article-next', className)} aria-label={label}>
      {prev && link(prev, prevLabel, false)}
      {next && link(next, nextLabel, true)}
    </nav>
  );
}

/* ---------------- Article layout (Layouts README, D-36) ----------------
   `ArticleLayout` › nav (search, `ArticleNavGroup`s of `ArticleNavLink`s) + main › `Article`.
   The main column is a <div>, not a <main>: the layout renders inside the shell's own <main>
   (`.mv-canvas`), and a second, nested main landmark is what screen readers would get.
   Compact — the layout's OWN width under 760px (beside the Ask dock, in a narrow window, on a
   phone), or `compact` forced by the screen — it is one column and the nav a row above the
   article holding `compactNav` (search + a Contents menu) in place of `nav` (MAR-381). The
   width is measured before paint, so there is no frame of the split layout in a narrow box. */
const ARTICLE_COMPACT_BELOW = 760;
const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;
const ArticleLayout = React.forwardRef(function ArticleLayout({ nav, compactNav, compact: forced = false, navLabel = 'Articles', mainRef, children, className = '', ...rest }, ref) {
  const own = React.useRef(null);
  const [narrowBox, setNarrowBox] = React.useState(false);
  useIsoLayoutEffect(() => {
    const el = own.current; if (!el) return undefined;
    const read = () => { const w = el.getBoundingClientRect().width; setNarrowBox(w > 0 && w < ARTICLE_COMPACT_BELOW); };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const setRef = React.useCallback((el) => {
    own.current = el;
    if (typeof ref === 'function') ref(el); else if (ref) ref.current = el;
  }, [ref]);
  const compact = forced || narrowBox;
  return (
    <div ref={setRef} {...rest} className={cx('mv-article-layout', compact && 'mv-article-layout--compact', className)} data-compact={compact ? 'true' : undefined}>
      <nav className="mv-article-nav" aria-label={navLabel}>{compact && compactNav != null ? compactNav : nav}</nav>
      <div ref={mainRef} className="mv-article-main">{children}</div>
    </div>
  );
});
/** One labelled group of article links in the nav. */
function ArticleNavGroup({ label, children, className = '', ...rest }) {
  return (
    <div {...rest} className={cx('mv-article-group', className)}>
      {label != null && <div className="mv-article-group-label">{label}</div>}
      {children}
    </div>
  );
}
/** An article link: a real link (focusable, opens in a new tab); `current` marks the open one. */
function ArticleNavLink({ href, current = false, onClick, children, className = '', ...rest }) {
  return (
    <a {...rest} className={cx('mv-article-link', className)} href={href} onClick={onClick} aria-current={current ? 'page' : undefined}>
      {children}
    </a>
  );
}
/** The article itself: breadcrumb, title, lede, then the body (children — prose, ArticlePager). */
function Article({ breadcrumb, title, lede, children, className = '', ...rest }) {
  return (
    <article {...rest} className={cx('mv-article', className)}>
      {breadcrumb != null && <div className="mv-article-bc">{breadcrumb}</div>}
      {title != null && <h1>{title}</h1>}
      {lede != null && <p className="mv-article-lede">{lede}</p>}
      {children}
    </article>
  );
}

/* ---------------- List ----------------
   A plain bulleted (or numbered) list outside prose — evidence lines, notes, a short history.
   The reset strips list markers from a bare `<ul>`; this puts them back on tokens. */
function List({ ordered = false, children, className = '', ...rest }) {
  const Tag = ordered ? 'ol' : 'ul';
  return <Tag {...rest} className={cx('mv-bullets', ordered && 'mv-bullets--ordered', className)}>{children}</Tag>;
}

/* ---------------- Preview frame (D-42) ----------------
   A framed preview of a document the merchant's customers receive. Only the frame is the
   app's; the content is drawn by its own renderer. Heights are the caller's, per breakpoint. */
function PreviewFrame({ title, srcDoc, src, height = 620, compactHeight = 520, naturalWidth, fitContent = false, className = '', ...rest }) {
  const px = (v) => (typeof v === 'number' ? `${v}px` : v);
  // ds-dynamic-style: the frame's heights are the caller's (per document), read by settings.css.
  const vars = { '--mv-preview-h': px(height), '--mv-preview-h-compact': px(compactHeight) };
  if (!naturalWidth) return <iframe {...rest} title={title} srcDoc={srcDoc} src={src} className={cx('mv-preview-frame', className)} style={vars} />;
  return <ScaledPreviewFrame {...rest} title={title} srcDoc={srcDoc} src={src} naturalWidth={naturalWidth}
    height={height} compactHeight={compactHeight} fitContent={fitContent} className={className} />;
}
/* The frame's top + bottom border (settings.css `.mv-preview-frame`, border-box). */
const PREVIEW_FRAME_BORDER = 2;
/* How tall the document is at its natural width, or null when it cannot be read: a frame
   sandboxed WITHOUT allow-same-origin, or a cross-origin src, has no readable contentDocument
   (null, or a SecurityError). A sandbox that keeps allow-same-origin is readable. The frame is
   collapsed to 0 for the read so the viewport cannot pad the answer — `scrollHeight` is never
   less than the viewport, and an email's `html,body{height:100%}` would otherwise measure the
   box rather than the email. Synchronous: no frame is painted at 0. */
function measureDocHeight(frame) {
  try {
    const doc = frame.contentDocument;
    const root = doc && doc.documentElement;
    if (!root) return null;
    const was = frame.style.height;
    frame.style.height = '0px';
    const h = Math.max(root.scrollHeight, doc.body ? doc.body.scrollHeight : 0);
    frame.style.height = was;
    return h > 0 ? h : null;
  } catch {
    return null;
  }
}
/* A document laid out for a fixed width (an email is a 600px card) previewed in a narrower box:
   the iframe is drawn at its natural width and scaled down to fit, like a thumbnail, rather than
   reflowed into the box (MAR-399). Never scaled UP. The box keeps the caller's heights; the
   iframe is made taller by 1/scale so its scaled height fills the box.
   `fitContent` (MAR-400): once the document has loaded, the box is as tall as the SCALED
   document — capped at the caller's heights, past which the document scrolls inside — so a short
   email does not sit on top of an empty box. A document that cannot be measured (a sandbox
   without allow-same-origin, cross-origin) keeps the caller's heights. Measured on every load
   (a new srcDoc re-loads) and every scale change. */
function ScaledPreviewFrame({ title, srcDoc, src, naturalWidth, height, compactHeight, fitContent, className, onLoad, ...rest }) {
  const box = React.useRef(null);
  const frame = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  const [docHeight, setDocHeight] = React.useState(null);
  useIsoLayoutEffect(() => {
    const el = box.current; if (!el) return undefined;
    // clientWidth: inside the border, and unaffected by a transformed ancestor (a drawer's slide-in).
    const read = () => { const w = el.clientWidth; if (w > 0) setScale(Math.min(1, w / naturalWidth)); };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read); ro.observe(el);
    return () => ro.disconnect();
  }, [naturalWidth]);
  const measure = React.useCallback(() => {
    if (fitContent && frame.current) setDocHeight(measureDocHeight(frame.current));
  }, [fitContent]);
  // A new document is unmeasured until it loads; the old height must not frame it.
  useIsoLayoutEffect(() => { setDocHeight(null); }, [srcDoc, src]);
  // Re-read when the scale changes (a loaded document only; an unloaded one reads null anyway).
  useIsoLayoutEffect(() => { if (docHeight !== null) measure(); }, [scale]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleLoad = (e) => { measure(); if (onLoad) onLoad(e); };
  const px = (v) => (typeof v === 'number' ? `${v}px` : v);
  const fit = fitContent && docHeight ? Math.ceil(docHeight * scale) + PREVIEW_FRAME_BORDER : null;
  const cap = (limit) => (fit === null ? px(limit) : typeof limit === 'number' ? `${Math.min(fit, limit)}px` : `min(${fit}px, ${limit})`);
  // ds-dynamic-style: the box's heights are the caller's, or the measured document's under them.
  const vars = { '--mv-preview-h': cap(height), '--mv-preview-h-compact': cap(compactHeight) };
  // ds-dynamic-style: the scale is measured from the box, the natural width is the caller's document's.
  const frameVars = { width: `${naturalWidth}px`, height: `calc(100% / ${scale})`, transform: `scale(${scale})` };
  return (
    <div ref={box} className={cx('mv-preview-frame', 'mv-preview-frame--scaled', className)} style={vars}
      data-scale={scale.toFixed(3)} data-fit={fit === null ? undefined : fit}>
      <iframe {...rest} ref={frame} title={title} srcDoc={srcDoc} src={src} className="mv-preview-frame-doc" style={frameVars} onLoad={handleLoad} />
    </div>
  );
}

export { Reading, ReadingSection, Hint, FieldLabel, MessageBody, EmptyValue, Meta, Code, BootLoader, ArticleLayout, ArticleNavGroup, ArticleNavLink, Article, ArticlePager, List, PreviewFrame };
